import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Redirect, Link } from 'react-router-dom';
import classNames from 'classnames';
import ClipLoader from 'react-spinners/ClipLoader';

import { useFormatMessage, useFormatDate } from 'hooks';
import Table from 'components/Table';
import { fetchEntities, entitiesCleanUp } from 'state/actions/users';
import paths from 'pages/Router/paths';

import { search as searchInObject } from 'utils';
import CONSTANTS from 'utils/constants';
import classes from './Users.module.scss';

const Users = () => {
  const entitiesName = 'users';
  const i18nEntityName = 'Users';

  const { entitiesList, isAdmin, error, loading } = useSelector(
    (state) => ({
      entitiesList: state[entitiesName].data,
      isAdmin: CONSTANTS.IS_USER_ROLE_ADMIN[state.auth.userData.role],
      error: state[entitiesName].error,
      loading: state[entitiesName].loading,
    }),
    shallowEqual
  );

  const dispatch = useDispatch();

  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchEntities());
    }

    return () => dispatch(entitiesCleanUp());
  }, [dispatch, isAdmin]);

  const columns = [
    {
      Header: useFormatMessage(`${i18nEntityName}.name`),
      accessor: 'name',
    },
    {
      Header: useFormatMessage(`${i18nEntityName}.email`),
      accessor: 'email',
    },
    {
      Header: useFormatMessage(`${i18nEntityName}.role`),
      accessor: 'role',
      Cell: ({ row }) => {
        const { role } = row.original;
        const roleAsString = CONSTANTS.USERS_ROLES_AS_STRING[role];

        return (
          <small className="has-text-grey is-abbr-like">{roleAsString}</small>
        );
      },
    },
    {
      Header: useFormatMessage(`${i18nEntityName}.created`),
      accessor: 'createdAt',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {useFormatDate(row.original.createdAt)}
        </small>
      ),
    },
    {
      Header: '',
      id: 'actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div className="buttons is-right">
          <Link
            to={`/${entitiesName}/${row.original.id}/profile`}
            className="button is-small is-primary"
          >
            <span className="icon is-small">
              <i className="mdi mdi-eye" />
            </span>
          </Link>
        </div>
      ),
      disableSortBy: true,
    },
  ];

  const data = search
    ? entitiesList.filter((el) => {
        const clonedElem = { ...el };

        // Delete these fields from search
        delete clonedElem.id;
        return searchInObject(clonedElem, search);
      })
    : entitiesList;

  const redirect = !isAdmin && <Redirect to={paths.ROOT} />;

  return (
    <>
      {redirect}

      <section className="hero is-hero-bar">
        <div className="hero-body">
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <h1 className="title">
                  {useFormatMessage(`${i18nEntityName}.entities`)} (
                  {entitiesList.length})
                </h1>
              </div>
            </div>
            <div className="level-right">
              <div className="level-item">
                <Link to={paths.ADD_USER} className="button">
                  {useFormatMessage(`${i18nEntityName}.newEntity`)}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="section is-main-section">
        <div className="card has-table has-mobile-sort-spaced">
          <header className="card-header">
            <p className={classNames('card-header-title', classes.tableHeader)}>
              <span>{useFormatMessage(`${i18nEntityName}.search`)}</span>
              <input
                type="text"
                className="input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </p>
          </header>
          <div className="b-table">
            {loading ? <ClipLoader /> : <Table columns={columns} data={data} />}
            {error && 'Show error'}
          </div>
        </div>
      </section>
    </>
  );
};

export default Users;
